import { keepPreviousData, useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./client";
import { fetchAllPages, wantsAllPages } from "./fetchAllPages";
import type { ListResponse, SingleResponse } from "./types";

export interface User {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string; // viewer | operator | tenant_admin | super_admin
  status: string; // active | pending | inactive
  createdAt: string;
  updatedAt: string;
}

// AdminUser is a platform-user row as GET /admin/users returns it: the
// user plus its tenantId. The company's name is not on the row — pages
// look it up from the company list.
export interface AdminUser extends User {
  tenantId: string;
}

interface ListUsersParams {
  limit?: number;
  cursor?: string;
  role?: string;
}

// Users are a bounded collection shown in a table with in-browser search,
// so without limit/cursor these hooks load every page — searching one page
// would hide everyone past it.
export function useUsers(params: ListUsersParams = {}) {
  const qs = new URLSearchParams();
  if (params.limit) qs.set("limit", String(params.limit));
  if (params.cursor) qs.set("cursor", params.cursor);
  if (params.role) qs.set("role", params.role);
  const query = qs.toString();
  const url = `/api/v1/users${query ? `?${query}` : ""}`;

  return useQuery({
    queryKey: ["users", params],
    queryFn: () => (wantsAllPages(params) ? fetchAllPages<User>(url) : apiFetch<ListResponse<User>>(url)),
    placeholderData: keepPreviousData,
  });
}

export interface InviteUserBody {
  email: string;
  role: string;
  firstName?: string;
  lastName?: string;
}

export function useInviteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: InviteUserBody) =>
      apiFetch<SingleResponse<User>>("/api/v1/users", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
}

export interface UpdateUserVars {
  userId: string;
  // Super-admin (cross-tenant) pages set this so the op targets the user's own
  // tenant via the X-Acting-Tenant-ID header; tenant pages omit it.
  actingTenantId?: string;
  role?: string;
  status?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

// Update a user's role, status (active | deactivated), name, or email. Send
// only the fields you want to change.
export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, actingTenantId, ...body }: UpdateUserVars) => {
      // Never fire a request with a blank id — it would hit /users/ (no id) and
      // the gateway rejects it with an opaque 403 instead of a clear error.
      if (!userId) return Promise.reject(new Error("Missing user id — cannot update this user."));
      return apiFetch<SingleResponse<User>>(`/api/v1/users/${userId}`, {
        method: "PUT",
        ...(actingTenantId ? { headers: { "X-Acting-Tenant-ID": actingTenantId } } : {}),
        body: JSON.stringify(body),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    },
  });
}

// Soft-delete a user — the backend disables their Cognito login and tombstones
// the record, so they drop out of the active list. Pass actingTenantId on the
// super-admin (cross-tenant) page so the op targets the user's own tenant.
export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, actingTenantId }: { userId: string; actingTenantId?: string }) => {
      if (!userId) return Promise.reject(new Error("Missing user id — cannot delete this user."));
      return apiFetch<void>(`/api/v1/users/${userId}`, {
        method: "DELETE",
        ...(actingTenantId ? { headers: { "X-Acting-Tenant-ID": actingTenantId } } : {}),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    },
  });
}

export function useAdminInviteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: InviteUserBody & { tenantId: string }) =>
      apiFetch<SingleResponse<User>>("/api/v1/users", {
        method: "POST",
        headers: { "X-Acting-Tenant-ID": body.tenantId },
        body: JSON.stringify({ email: body.email, role: body.role, firstName: body.firstName, lastName: body.lastName }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      qc.invalidateQueries({ queryKey: ["users"] });
    },
  });
}

// Server-side filters for the platform user list. They apply across every
// page — the list scales with tenants × users per tier, so it is paged and
// filtered on the server, never loaded whole and filtered in the browser.
export interface AdminUserFilters {
  search?: string;
  searchField?: "email" | "name";
  tenantIds?: string[];
  roles?: string[];
  status?: string;
}

export function adminUserFilterParams(filters: AdminUserFilters = {}): URLSearchParams {
  const qs = new URLSearchParams();
  const search = filters.search?.trim();
  if (search) {
    qs.set("search", search);
    qs.set("searchField", filters.searchField ?? "email");
  }
  const tenantIds = [...new Set((filters.tenantIds ?? []).filter(Boolean))].sort();
  if (tenantIds.length > 0) qs.set("tenantId", tenantIds.join(","));
  const roles = [...new Set((filters.roles ?? []).filter(Boolean))].sort();
  if (roles.length > 0) qs.set("role", roles.join(","));
  if (filters.status) qs.set("status", filters.status);
  return qs;
}

// One page of platform users matching `filters`.
export function useAdminUsers(
  options: { limit?: number; cursor?: string; filters?: AdminUserFilters; enabled?: boolean } = {},
) {
  const qs = adminUserFilterParams(options.filters);
  if (options.limit) qs.set("limit", String(options.limit));
  if (options.cursor) qs.set("cursor", options.cursor);
  const query = qs.toString();

  return useQuery({
    queryKey: ["admin-users", "page", query],
    queryFn: () => apiFetch<ListResponse<AdminUser>>(`/api/v1/admin/users${query ? `?${query}` : ""}`),
    placeholderData: keepPreviousData,
    enabled: options.enabled ?? true,
  });
}

// How many platform users match `filters` across every page (countOnly).
export function useAdminUserTotal(filters: AdminUserFilters = {}, options: { enabled?: boolean } = {}) {
  const qs = adminUserFilterParams(filters);
  const filterKey = qs.toString();
  qs.set("countOnly", "true");

  return useQuery({
    queryKey: ["admin-users", "total", filterKey],
    queryFn: async () => {
      const res = await apiFetch<ListResponse<AdminUser>>(`/api/v1/admin/users?${qs.toString()}`);
      // undefined, not 0, when the server sent no total: "… " beats "of 0".
      return { total: res.pagination.total, isLowerBound: res.pagination.totalIsLowerBound ?? false };
    },
    placeholderData: keepPreviousData,
    enabled: options.enabled ?? true,
  });
}
